/**
 * Shared cover-variant stock/price classification used by the Simple PDP cover swatch grid
 * AND its "+N more" overflow flyout so both surfaces display identically.
 *
 * Rules (FSD §1.4 + made-to-order):
 *   - Tracked stock value (ATS is a real number < sentinel): In stock / Low stock / Sold out.
 *   - Made-to-order / perpetual (perpetual flag, sentinel-high ATS, or no inventory record):
 *     show the cover price rule instead of a stock badge.
 */

// Made-to-order / non-stock-tracked (perpetual) variants report a sentinel ATS. Treat any ATS at
// or above this as perpetual even when the SFRA `perpetual` flag is absent (e.g. OCI, where
// isPerpetual() always returns false but King uses a large sentinel stock level instead).
export const PERPETUAL_ATS_THRESHOLD = 9999999;

/**
 * Perpetual = orderable but not stock-tracked (made-to-order). These show the cover price rule
 * instead of stock badges. Detected via the SFRA `perpetual` flag or a sentinel-high ATS.
 *
 * @param {Object} variant
 * @returns {boolean}
 */
export function isPerpetualVariant(variant) {
  if (!variant) return false;
  if (variant.orderable === false) return false;
  if (variant.perpetual === true) return true;

  const ats = typeof variant.ats === 'number' ? variant.ats : null;

  return ats != null && ats >= PERPETUAL_ATS_THRESHOLD;
}

/**
 * Classify a cover variant for display.
 *
 * Priority (made-to-order ALWAYS wins, even when ATS is 0/low):
 *   1. `perpetual` — perpetual flag, sentinel-high ATS, or no inventory record at all
 *      (orderable but no tracked stock value). Renders the price rule, never a stock badge.
 *   2. `oos` — not orderable, or a real tracked ATS <= 0.
 *   3. `low-stock` / `in-stock` — real tracked ATS.
 *
 * @param {Object} variant
 * @returns {'in-stock'|'low-stock'|'oos'|'perpetual'}
 */
export function getStockLevel(variant) {
  if (!variant) return 'oos';
  if (variant.orderable === false) return 'oos';

  // Made-to-order / perpetual must be evaluated BEFORE the OOS check: these are always available
  // regardless of ATS (a perpetual variant can legitimately report ATS 0).
  if (isPerpetualVariant(variant)) return 'perpetual';

  const ats = typeof variant.ats === 'number' ? variant.ats : null;

  // No tracked stock value but orderable (no inventory record) → made-to-order → price rule.
  if (ats == null) return 'perpetual';

  if (ats <= 0) return 'oos';
  if (ats <= 4) return 'low-stock';

  return 'in-stock';
}

/**
 * Cover price rule (mirrors the Customize Your Own flyout cover tile): the amount this variant
 * exceeds the cheapest variant (`masterMinPrice`), or `null` when there is no surcharge.
 *
 * @param {Object} variant
 * @param {number|null|undefined} masterMinPrice
 * @returns {number|null}
 */
export function getCoverExtraPrice(variant, masterMinPrice) {
  if (masterMinPrice == null) return null;

  const salePrice = typeof variant?.salePrice === 'number' ? variant.salePrice : null;

  if (salePrice == null || salePrice <= 0) return null;

  const delta = salePrice - masterMinPrice;

  return delta > 0 ? delta : null;
}

const COVER_STOCK_MESSAGE_GRID_CLASS = 'cover-swatch-grid__tile-stock-message';
const COVER_STOCK_MESSAGE_FLYOUT_CLASS = 'cover-variant-tile-content-stock-message';

/**
 * BEM class for the cover tile stock/price line. Modifiers drive status colour in theme CSS:
 *   --in-stock  → success.dark  (#1E5F35) "Available"
 *   --low-stock → warning.dark  (#92400E) "Only N left"
 *   --sold-out  → error.standard (#9D2A1E) "Sold out"
 *
 * @param {'in-stock'|'low-stock'|'oos'|'perpetual'} stockLevel
 * @param {{variant?: 'grid'|'flyout'}} [options]
 * @returns {string}
 */
export function getCoverStockMessageClassName(stockLevel, {variant = 'grid'} = {}) {
  const base =
    variant === 'flyout' ? COVER_STOCK_MESSAGE_FLYOUT_CLASS : COVER_STOCK_MESSAGE_GRID_CLASS;

  if (stockLevel === 'in-stock') {
    return `${base} ${base}--in-stock`;
  }

  if (stockLevel === 'low-stock') {
    return `${base} ${base}--low-stock`;
  }

  if (stockLevel === 'oos') {
    return `${base} ${base}--sold-out`;
  }

  return base;
}

/**
 * CoverSwatchGrid theme part for the stock/price line.
 *
 * @param {'in-stock'|'low-stock'|'oos'|'perpetual'} stockLevel
 * @returns {'tileStockMessage'|'tilePriceMessage'|'tileStockMessageLow'|'tileStockMessageOOS'}
 */
export function getCoverStockMessageStyleKey(stockLevel) {
  if (stockLevel === 'perpetual') return 'tilePriceMessage';
  if (stockLevel === 'low-stock') return 'tileStockMessageLow';
  if (stockLevel === 'oos') return 'tileStockMessageOOS';

  return 'tileStockMessage';
}
